import { mutateDefect } from "@/api/defect/useDefectsQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { OperationAutoComplete } from "@/features/ui/autoComplete/operation/operation-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import styled from "@emotion/styled";
import { Box, Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DefaultApiDefectsPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const DefectCreateForm = () => {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: postMutate } = useMutation(
    (params: DefaultApiDefectsPostRequest) =>
      mutateDefect.create(params).mutationFn(params as DefaultApiDefectsPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["defects"]);
        closeModal(form.values);
        customAlert(
          `${t("message.createSuccess", { item: `${t("defects.defects")}` })}`,
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("defect.code") }),
          `${t("common.createFail", { item: `${t("defect.defect")}` })}`,
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      code: undefined,
      name: undefined,
      operationCode: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      postMutate({
        defectsGetRequest: {
          code: form.values.code!,
          name: form.values.name!,
          operationCode: form.values.operationCode!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("defect.code") }),
        `${t("common.createFail", { item: `${t("defect.defect")}` })}`,
        "red",
      );
    }
  };

  return (
    <Box w={320} mx="auto">
      <Form>
        <ModalFieldSet>
          <TextInput
            withAsterisk
            label={t("defect.code")}
            placeholder={t("placeholder.input", { item: `${t("defect.code")}` })}
            {...form.getInputProps("code")}
          />
          <TextInput
            withAsterisk
            label={t("defect.name")}
            placeholder={t("placeholder.input", { item: `${t("defect.name")}` })}
            {...form.getInputProps("name")}
          />
          <OperationAutoComplete
            label={t("operation.code")}
            placeholder={t("placeholder.select", { item: `${t("operation.code")}` })}
            {...form.getInputProps("operationCode")}
          />
          <ModalConfirmBtnBox>
            <Button color="gray" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={validateFormValues({
                pageType: "defects",
                values: form.values,
              })}
              onClick={onSubmit}
            >
              {t("common.create", { item: `${t("defect.code")}` })}
            </Button>
          </ModalConfirmBtnBox>
        </ModalFieldSet>
      </Form>
    </Box>
  );
};

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const ModalFieldSet = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalConfirmBtnBox = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;
